import { Clamp } from '@nova/utils';
import { IMediaInstance, PlayOptions, sound } from '@pixi/sound';

import { AudioManager } from './AudioManager';
import { IAudioManager } from './IAudioManager';

/**
 * SFX
 *
 * Handles all SFX instances currently playing in the game.
 */
export class SFX implements IAudioManager {
  private _currentSFX: { [id: string]: IMediaInstance };

  /**
   * Gets the mute state of all SFX
   */
  public isMuted(): boolean {
    return AudioManager.audioSettings?.sfx?.mute ?? false;
  }

  /**
   * Gets the global volume for all SFX
   */
  public get volume(): number {
    return AudioManager.audioSettings?.sfx?.volume ?? 0.5;
  }

  /**
   * Sets the global volume for all SFX
   */
  public set volume(volume: number) {
    const updatedVolume: number = Clamp(volume, 0, 1);
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id].volume = updatedVolume;
      }
    }

    if (AudioManager.audioSettings?.sfx) {
      AudioManager.audioSettings.sfx.volume = updatedVolume;
      AudioManager.saveSettings();
    }
  }

  public constructor() {
    this._currentSFX = {};

    // Reset mute state when the game is visible again or in focus.
    document.addEventListener('visibilitychange', this.onVisibilitychange.bind(this));
    globalThis.addEventListener('focus', this.onFocus.bind(this));
  }

  private onVisibilitychange(): void {
    if (document.visibilityState === 'visible') {
      if (AudioManager.audioSettings?.sfx?.mute) {
        this.mute();
      }
      else {
        this.unmute();
      }
    }
  }

  private onFocus(): void {
    if (!AudioManager.audioSettings?.sfx?.mute) {
      this.unmute();
    }
  }

  /**
   * Mutes all currently playing SFX.
   */
  public mute(): void {
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id].muted = true;
      }
    }

    if (AudioManager.audioSettings?.sfx) {
      AudioManager.audioSettings.sfx.mute = true;
      AudioManager.saveSettings();
    }
  }

  /**
   * UnMutes all currently playing SFX.
   */
  public unmute(): void {
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id].muted = false;
      }
    }

    if (AudioManager.audioSettings?.sfx) {
      AudioManager.audioSettings.sfx.mute = false;
      AudioManager.saveSettings();
    }
  }

  /**
   * Plays a given sfx with the provided options.
   * If the sound effect was not loaded then it will first be loaded before playing.
   *
   * @param sfx - The SFX to play
   * @param options - The options to apply to the SFX
   * @returns the instance of the SFX.
   */
  public async play(sfx: string, options?: PlayOptions): Promise<IMediaInstance> {
    const volume: number = (options?.volume ?? 1) * this.volume;
    const muted: boolean = AudioManager.audioSettings?.sfx?.mute ?? false;
    try {
      const sfxInstance: IMediaInstance = await sound.play(sfx, { ...options, volume, muted });
      this._currentSFX[sfx] = sfxInstance;
      sfxInstance.once('end', () => {
        delete this._currentSFX[sfxInstance.id];
      });

      return sfxInstance;
    }
    catch (error) {
      throw new Error(`Failed to play SFX: ${sfx}`, { cause: error });
    }
  }

  /**
   * Stop all currently playing SFX.
   */
  public stop(): void {
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id].stop();
      }
    }
    this._currentSFX = {};
  }

  /**
   * Stop a specific SFX.
   * @param sfx - The SFX to stop
   * @returns void
   */
  public stopSFX(sfx: string): void {
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        if (id === sfx) {
          this._currentSFX[id].stop();
          delete this._currentSFX[id];
        }
      }
    }
  }

  /**
   * Pause all currently playing SFX.
   */
  public pause(): void {
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id].paused = true;
      }
    }
  }

  /**
   * Resume all currently playing SFX.
   */
  public resume(): void {
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id].paused = false;
      }
    }
  }

  /**
   * Destroys the SFX manager and cleans up resources.
   */
  public destroy(): void {
    // Stop all SFX
    for (const id in this._currentSFX) {
      if (Object.prototype.hasOwnProperty.call(this._currentSFX, id)) {
        this._currentSFX[id]?.destroy();
      }
    }

    // Clear references
    this._currentSFX = {};

    // Remove event listeners
    document.removeEventListener('visibilitychange', this.onVisibilitychange.bind(this));
    globalThis.removeEventListener('focus', this.onFocus.bind(this));
  }
}
